import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase, getSignedUrl } from '../../lib/supabase';
import { 
  ArrowLeft,
  Search,
  Filter,
  Edit2,
  Trash2,
  FileText,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink
} from 'lucide-react';

// Componente para enlaces de tickets con URLs firmadas
const TicketLink = ({ ticketPath }) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticketPath) {
      setLoading(false);
      return;
    }

    const fetchSignedUrl = async () => {
      try {
        setLoading(true);
        const url = await getSignedUrl('tickets', ticketPath);
        if (url) {
          setSignedUrl(url);
        }
      } catch (err) {
        console.error('Error cargando ticket:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSignedUrl();
  }, [ticketPath]);

  if (loading) {
    return <span className="text-gray-400 text-xs">Cargando...</span>;
  }

  if (!signedUrl) {
    return <span className="text-gray-400 text-xs">Error</span>;
  }

  return (
    <a
      href={signedUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
    >
      <FileText className="w-4 h-4" />
      <ExternalLink className="w-3 h-3" />
    </a>
  );
};

const TransactionsList = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('todas');
  const [filterTeam, setFilterTeam] = useState('Todos');
  const [filterStatus, setFilterStatus] = useState('todas');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const teams = ['Todos', 'General', 'Juvenil', 'Cadete', 'Amateur'];

  useEffect(() => {
    fetchTransactions();
  }, [filterType, filterTeam, filterStatus]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('transactions')
        .select('*')
        .order('date', { ascending: false });

      if (filterType !== 'todas') {
        query = query.eq('type', filterType);
      }

      if (filterTeam !== 'Todos') {
        query = query.eq('team', filterTeam);
      }

      if (filterStatus === 'aprobadas') {
        query = query.eq('approved', true);
      } else if (filterStatus === 'pendientes') {
        query = query.eq('approved', false);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error al cargar transacciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!transactionToDelete) return;

    try {
      setDeleting(true);

      // Si tiene ticket, eliminarlo del storage
      if (transactionToDelete.ticket_url) {
        const filename = transactionToDelete.ticket_url;
        await supabase.storage.from('tickets').remove([filename]);
      }

      // Eliminar transacción
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', transactionToDelete.id);

      if (error) throw error;

      // Actualizar lista
      setTransactions(transactions.filter(t => t.id !== transactionToDelete.id));
      setShowDeleteModal(false);
      setTransactionToDelete(null);
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert('Error al eliminar la transacción');
    } finally {
      setDeleting(false);
    }
  };

  const handleApprove = async (transaction) => {
    try {
      const { error } = await supabase
        .from('transactions')
        .update({ 
          approved: true,
          approved_by: userProfile.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', transaction.id);

      if (error) throw error;

      // Actualizar estado local
      setTransactions(transactions.map(t => 
        t.id === transaction.id 
          ? { ...t, approved: true, approved_by: userProfile.id, approved_at: new Date().toISOString() }
          : t
      ));
    } catch (error) {
      console.error('Error al aprobar:', error);
      alert('Error al aprobar la transacción');
    }
  };

  const filteredTransactions = transactions.filter(t => {
    const searchLower = searchTerm.toLowerCase();
    return (
      t.description.toLowerCase().includes(searchLower) ||
      t.category.toLowerCase().includes(searchLower) ||
      t.amount.toString().includes(searchLower)
    );
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando transacciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/finance')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Todas las Transacciones</h1>
                <p className="text-sm text-gray-600">{filteredTransactions.length} transacciones encontradas</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Barra de búsqueda y filtros */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Búsqueda */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por descripción, categoría o monto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Filtro por tipo */}
            <div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="todas">Todos los tipos</option>
                <option value="ingreso">Ingresos</option>
                <option value="gasto">Gastos</option>
              </select>
            </div>

            {/* Filtro por equipo */}
            <div>
              <select
                value={filterTeam}
                onChange={(e) => setFilterTeam(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {teams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Filtro por estado de aprobación */}
          {userProfile?.role === 'admin' && (
            <div className="flex gap-2">
              <button
                onClick={() => setFilterStatus('todas')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === 'todas'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas
              </button>
              <button
                onClick={() => setFilterStatus('aprobadas')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === 'aprobadas'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Aprobadas
              </button>
              <button
                onClick={() => setFilterStatus('pendientes')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === 'pendientes'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pendientes
              </button>
            </div>
          )}
        </div>

        {/* Lista de transacciones */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Fecha</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Tipo</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Descripción</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Categoría</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Equipo</th>
                  <th className="text-right py-4 px-6 text-sm font-semibold text-gray-700">Monto</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Ticket</th>
                  {userProfile?.role === 'admin' && (
                    <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Estado</th>
                  )}
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-6 text-sm text-gray-600">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        transaction.type === 'ingreso'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {transaction.type === 'ingreso' ? 'Ingreso' : 'Gasto'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-900 max-w-xs truncate">
                      {transaction.description}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-600">
                      {transaction.category}
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        {transaction.team}
                      </span>
                    </td>
                    <td className={`py-4 px-6 text-sm font-semibold text-right ${
                      transaction.type === 'ingreso' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'ingreso' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="py-4 px-6 text-center">
                      {transaction.ticket_url ? (
                        <TicketLink ticketPath={transaction.ticket_url} />
                      ) : (
                        <span className="text-gray-400 text-xs">Sin ticket</span>
                      )}
                    </td>
                    {userProfile?.role === 'admin' && (
                      <td className="py-4 px-6 text-center">
                        {transaction.approved ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                            <CheckCircle className="w-4 h-4" />
                            Aprobada
                          </span>
                        ) : (
                          <button
                            onClick={() => handleApprove(transaction)}
                            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 text-xs font-medium"
                          >
                            <Clock className="w-4 h-4" />
                            Pendiente
                          </button>
                        )}
                      </td>
                    )}
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => navigate(`/finance/transactions/edit/${transaction.id}`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {userProfile?.role === 'admin' && (
                          <button
                            onClick={() => {
                              setTransactionToDelete(transaction);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredTransactions.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No se encontraron transacciones</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Eliminar Transacción</h3>
                <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Descripción:</strong> {transactionToDelete?.description}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Monto:</strong> {formatCurrency(transactionToDelete?.amount)}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setTransactionToDelete(null);
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsList;